import { AxiosInstance } from "axios";
import { makeFormData } from "../components/utils/formData";
import { BaseHTTP } from "./base.http";

export interface CreditCardMovement {
  fecha: string;
  lugarOperacion?: string;
  codigoReferencia?: string;
  descripcion: string;
  montoOperacion: number;
  montoTotalAPagar: number;
  numeroCuota?: string;
  valorCuotaMensual: number;
  abono: number | null;
  cargo: number | null;
  comentario?: string;
  isNew?: boolean;
  similarExistente?: boolean;
  movimientoParecidoFecha?: string;
  movimientoParecidoDescripcion?: string;
  movimientoParecidoMonto?: number;
}

export interface CreditCardStatementExtraction {
  numeroTarjeta: string;
  banco?: string;
  periodoFacturadoDesde: string;
  periodoFacturadoHasta: string;
  movimientos: CreditCardMovement[];
}

export type CreditCardStatementStatus = 'processing' | 'needs_review' | 'completed' | 'failed';

export type CreditCardStatementDiscrepancyField = 'banco' | 'tarjeta' | 'periodo' | 'moneda';

export interface CreditCardStatementDiscrepancy {
  field: CreditCardStatementDiscrepancyField;
  selectedValue: string;
  extractedValue: string;
}

export interface CreditCardStatement {
  _id: string;
  businessId: number;
  bankId: number;
  creditCardId: number | null;
  periodoDesde: string;
  periodoHasta: string;
  status: CreditCardStatementStatus;
  errorMessage: string | null;
  periodoExtraidoDesde: string | null;
  periodoExtraidoHasta: string | null;
  periodoCoincide: boolean;
  numeroTarjetaExtraido: string;
  bancoExtraido: string | null;
  tarjetaCoincide: boolean;
  bancoCoincide: boolean;
  monedaExtraida: string | null;
  monedaCoincide: boolean;
  discrepancies: CreditCardStatementDiscrepancy[];
  fileName: string;
  movimientos: CreditCardMovement[];
  tokensUsed?: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveCreditCardStatementPayload {
  bankId: number;
  creditCardId?: number;
  periodoDesde: string;
  periodoHasta: string;
}

export interface ConfirmCreditCardStatementMovementPayload {
  fecha: string;
  lugarOperacion?: string;
  codigoReferencia?: string;
  descripcion: string;
  numeroCuota?: string;
  valorCuotaMensual: number;
  comentario?: string;
}

export interface ConfirmCreditCardStatementPayload {
  bankId: number;
  creditCardId?: number;
  periodoDesde: string;
  periodoHasta: string;
  movimientos: ConfirmCreditCardStatementMovementPayload[];
}

export class CreditCardStatementApi {

  private http !: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  async extract(file: File): Promise<CreditCardStatementExtraction> {
    const formData = makeFormData({ files: [file] })
    const response = await this.http.post(
      'v1/credit-card-statements/extract',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )

    return response.data
  }

  async estimateTokens(
    file: File,
    businessId?: number,
    creditCardId?: number
  ): Promise<{ estimatedTokens: number }> {
    const formData = makeFormData({
      files: [file],
      ...(businessId !== undefined && { businessId }),
      ...(creditCardId !== undefined && { creditCardId })
    })
    const response = await this.http.post(
      'v1/credit-card-statements/estimate-tokens',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )

    return response.data
  }

  async save(
    businessId: number,
    payload: SaveCreditCardStatementPayload,
    file: File
  ): Promise<CreditCardStatement> {
    const formData = makeFormData({ files: [file], ...payload })
    const response = await this.http.post(
      `v1/business/${businessId}/credit-card-statements`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )

    return response.data
  }

  async getStatus(businessId: number, id: string): Promise<CreditCardStatement> {
    const response = await this.http.get(`v1/business/${businessId}/credit-card-statements/${id}`)

    return response.data
  }

  async confirm(
    businessId: number,
    id: string,
    payload: ConfirmCreditCardStatementPayload
  ): Promise<CreditCardStatement> {
    const response = await this.http.patch(
      `v1/business/${businessId}/credit-card-statements/${id}/confirm`,
      payload
    )

    return response.data
  }

  async getIntroPreference(businessId: number): Promise<{ hideIntro: boolean }> {
    const response = await this.http.get(`v1/business/${businessId}/credit-card-statements/intro-preference`)

    return response.data
  }

  async setIntroPreference(businessId: number, hideIntro: boolean): Promise<{ hideIntro: boolean }> {
    const response = await this.http.patch(
      `v1/business/${businessId}/credit-card-statements/intro-preference`,
      { hideIntro }
    )

    return response.data
  }
}

export default new CreditCardStatementApi()
